// =============================================================================
// BookingFlow.tsx — PAY-6 / CLINIO-202
// =============================================================================
// Extended public booking widget that supports prepayment via Clinio Pay.
//
// Payment timing (when) is resolved separately from payment method (how):
//   clinic default → service override → resolvePaymentTiming.
//
// at_appointment: service → clinician → date → slot → patient → confirmation
//   Optional Brug klippekort when the patient has an eligible card.
//
// upon_booking: service → clinician → date → slot → patient → payment_method
//   → punch card settles via book-as-patient, or card/MobilePay via booking_intent
//   Appointment is confirmed only after settlement.
//
// Patient-facing UX for non-prepayment bookings is UNCHANGED (PAY-6 constraint).
// DKK only — no currency picker (PAY-1 ADD §8).
// =============================================================================

import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import {
  useBookableServices,
  useBookAppointment,
  type BookableService,
  type BookableClinician,
  type BookablePunchCardTemplate,
  type AvailableSlot,
} from '@/hooks/usePublicBooking';
import { useAvailableSlotsWeek } from '@/hooks/useAvailableSlotsWeek';
import {
  useCreateBookingIntent,
  useRequestBookingPayLink,
  useBookingIntentStatus,
} from '@/hooks/useBookingFlow';
import { useAuthenticatedPunchCardsForBooking } from '@/hooks/useAuthenticatedPunchCards';
import { usePurchasePunchCard } from '@/hooks/usePortalPunchCards';
import { toast } from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { primaryTelephoneNumber } from '@/lib/patientStamkortFields';
import { resolveClinicLogoUrl } from '@/lib/clinicBranding';
import {
  assertNever,
  parseBookingPaymentMethods,
  resolvePaymentTiming,
} from '@/lib/bookingPolicies';
import {
  catalogClinicianToSchedule,
  exceptionFromPublic,
  listBookablePractitionerDates,
} from '@/lib/clinicHours';
import { cliniciansForBookableService } from '@/lib/serviceBookingEligibility';
import {
  clinicianIdForOnlineBooking,
  onlineBookingStepsWithoutHiddenPractitioner,
  parseShowPractitionersInOnlineBooking,
} from '@/lib/onlineBookingPractitionerVisibility';
import {
  ClinicFallbackMark,
  PortalIcon,
  type PortalIconName,
} from '@/components/portal/PortalIcon';
import { WeekSlotPicker } from '@/components/portal/WeekSlotPicker';
import {
  addCivilDays,
  bookingCancellationHint,
  bookingServiceSummaryLabel,
  clinicCivilDate,
  firstBookableWeekMonday,
  formatClinicAddress,
  formatPortalDateTime,
  formatPortalDateTimeRange,
  formatPortalKroner,
  mondayOf,
  weekdaysMondayToFriday,
} from '@/lib/portalFormat';
import {
  pickDefaultPortalPaymentChoice,
  pickPreferredPunchCard,
  resolvePortalBookingPaymentContext,
  selectApplicablePunchCards,
  orderPaymentChoicesDefaultFirst,
  type PortalBookingPaymentChoice,
} from '@/lib/calendarPaymentChoice';
import { cn } from '@/utils/cn';

function servicePaymentWhenLabel(service: BookableService, clinicPaymentTiming?: unknown): string {
  const timing = resolvePaymentTiming({
    clinicPaymentTiming,
    servicePaymentTiming: service.payment_timing,
    serviceRequirePrepayment: service.require_prepayment,
  });
  return timing === 'upon_booking' ? 'betales ved booking' : 'betales ved behandlingen';
}

function clinicianInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function avatarTone(index: number): string {
  return ['av-a', 'av-b', 'av-c'][index % 3] ?? 'av-a';
}

// ─── Step definitions ─────────────────────────────────────────────────────────

type Step =
  | 'service'
  | 'clinician'
  | 'date'
  | 'slot'
  | 'patient'
  | 'payment_method'
  | 'payment' // prepayment step (redirect to Clinio Pay)
  | 'awaiting_payment' // post-redirect: polling for webhook confirmation
  | 'confirmation';

type VisualStepId = 'service' | 'clinician' | 'time' | 'patient';

const STANDARD_STEPS: Step[] = ['service', 'clinician', 'date', 'slot', 'patient', 'confirmation'];
const PREPAY_STEPS: Step[] = ['service', 'clinician', 'date', 'slot', 'patient', 'payment_method'];

function visualStepIdFor(step: Step): VisualStepId | null {
  switch (step) {
    case 'service':
      return 'service';
    case 'clinician':
      return 'clinician';
    case 'date':
    case 'slot':
      return 'time';
    case 'patient':
    case 'payment_method':
      return 'patient';
    case 'payment':
    case 'awaiting_payment':
    case 'confirmation':
      return null;
    default:
      return assertNever(step);
  }
}

// ─── Patient form schema ──────────────────────────────────────────────────────

const PatientSchema = z.object({
  first_name: z.string().min(1, 'Fornavn er påkrævet'),
  last_name: z.string().min(1, 'Efternavn er påkrævet'),
  email: z.string().email('Ugyldig e-mailadresse'),
  phone: z.string().optional(),
  notes: z.string().optional(),
});

type PatientFormValues = z.infer<typeof PatientSchema>;

type Translate = (k: string, options?: Record<string, unknown>) => string;

// ─── Component ────────────────────────────────────────────────────────────────

export function BookingFlow() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('service');
  const [selectedService, setSelectedService] = useState<BookableService | null>(null);
  const [selectedClinician, setSelectedClinician] = useState<BookableClinician | null>(null);
  const [anyClinicianSelected, setAnyClinicianSelected] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [weekMonday, setWeekMonday] = useState(() => mondayOf(clinicCivilDate(new Date())));

  // Prepayment flow state
  const [bookingIntentId, setBookingIntentId] = useState<string | null>(null);
  const [, setPayToken] = useState<string | null>(null);

  const [bookingError, setBookingError] = useState<string | null>(null);

  // Portal auth — punch cards are only shown to signed-in patients
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PortalBookingPaymentChoice | null>(null);
  const [paidOnline, setPaidOnline] = useState(false);
  const [portalPatientId, setPortalPatientId] = useState<string | null>(null);
  const [buyingTemplateId, setBuyingTemplateId] = useState<string | null>(null);
  const punchCardPurchase = usePurchasePunchCard();
  const autoBuyStarted = useRef(false);
  const submitLock = useRef(false);

  // ── Check if patient returned from Clinio Pay ─────────────────────────────
  // Clinio Pay redirects to ?pay_token=<token>&status=succeeded|cancelled
  // Punch-card shop returns use ?purchase=succeeded|cancelled — ignore those here.
  useEffect(() => {
    const purchaseStatus = searchParams.get('purchase');
    if (purchaseStatus === 'succeeded') {
      toast.success(t('booking.punch_card_purchase_success'));
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('purchase');
          next.delete('buy');
          next.delete('pay_token');
          next.delete('status');
          return next;
        },
        { replace: true },
      );
      return;
    }
    if (purchaseStatus === 'cancelled') {
      toast(t('booking.punch_card_purchase_cancelled'));
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('purchase');
          next.delete('pay_token');
          next.delete('status');
          return next;
        },
        { replace: true },
      );
      return;
    }

    const returnPayToken = searchParams.get('pay_token');
    const returnStatus = searchParams.get('status');

    if (returnPayToken && returnStatus === 'succeeded') {
      setPayToken(returnPayToken);
      setStep('awaiting_payment');
    } else if (returnPayToken && returnStatus === 'cancelled') {
      // Patient cancelled — return to the payment step, do not confirm.
      setStep('payment_method');
      setBookingError(t('booking.payment_cancelled'));
    }
  }, [searchParams, setSearchParams, t]);

  const {
    data: bookingData,
    isLoading: isLoadingServices,
    error: servicesError,
  } = useBookableServices(clinicSlug);

  const showPractitionerNames = parseShowPractitionersInOnlineBooking(
    bookingData?.settings.show_practitioners_in_online_booking,
  );
  const effectiveClinicianId =
    !showPractitionerNames || anyClinicianSelected ? null : (selectedClinician?.id ?? null);
  const eligibleClinicians = cliniciansForBookableService(
    bookingData?.clinicians ?? [],
    selectedService?.eligible_practitioner_ids,
  );

  const weekDays = weekdaysMondayToFriday(weekMonday);
  const showWeekPicker = step === 'date' || step === 'slot';
  const { slotsByDate, isLoading: isLoadingWeekSlots } = useAvailableSlotsWeek(
    clinicSlug,
    selectedService?.id ?? null,
    effectiveClinicianId,
    showWeekPicker ? weekDays : [],
  );

  // Standard booking (no prepayment)
  const bookMutation = useBookAppointment();

  // Prepayment flow mutations
  const createIntentMutation = useCreateBookingIntent();
  const requestPayLinkMutation = useRequestBookingPayLink();

  const { data: punchCardData, isLoading: isCheckingPunchCards } =
    useAuthenticatedPunchCardsForBooking(
      clinicSlug,
      selectedService?.id ?? null,
      Boolean(sessionEmail),
      bookingData?.clinic_id,
    );

  const handleBuyPunchCard = useCallback(
    async (templateId: string) => {
      if (!clinicSlug) return;
      if (!portalPatientId) {
        navigate(
          `/portal/login?from=${encodeURIComponent(`/book/${clinicSlug}?buy=${templateId}`)}`,
        );
        return;
      }
      setBuyingTemplateId(templateId);
      try {
        const result = await punchCardPurchase.mutateAsync({
          templateId,
          patientId: portalPatientId,
          returnPath: `/book/${clinicSlug}`,
        });
        window.location.href = result.checkout_url;
      } catch (err) {
        console.error('punch card purchase failed', err);
        toast.error(t('booking.punch_card_purchase_error'));
        setBuyingTemplateId(null);
      }
    },
    [clinicSlug, portalPatientId, navigate, punchCardPurchase, t],
  );

  useEffect(() => {
    const buyId = searchParams.get('buy');
    if (!buyId || !portalPatientId || autoBuyStarted.current) return;
    autoBuyStarted.current = true;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('buy');
        return next;
      },
      { replace: true },
    );
    void handleBuyPunchCard(buyId);
  }, [searchParams, portalPatientId, handleBuyPunchCard, setSearchParams]);

  useEffect(() => {
    const applySession = (email: string | null, signedIn: boolean) => {
      setSessionEmail(email);
      setIsAuthenticated(signedIn);
      if (!signedIn) {
        setPortalPatientId(null);
        setSelectedPayment(null);
      }
    };

    const checkAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      applySession(session?.user?.email ?? null, Boolean(session?.user));
    };
    void checkAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session?.user?.email ?? null, Boolean(session?.user));
    });

    return () => subscription.unsubscribe();
  }, []);

  // Poll booking_intent status after returning from Clinio Pay
  const { data: intentStatus } = useBookingIntentStatus(
    step === 'awaiting_payment' ? bookingIntentId : null,
    { enabled: step === 'awaiting_payment' },
  );

  // When booking is confirmed via webhook → advance to confirmation
  useEffect(() => {
    if (intentStatus?.status === 'confirmed' && step === 'awaiting_payment') {
      setPaidOnline(true);
      setStep('confirmation');
    }
    if (intentStatus?.status === 'expired' && step === 'awaiting_payment') {
      setStep('patient');
      setBookingError(t('booking.payment_expired'));
    }
  }, [intentStatus, step, t]);

  const form = useForm<PatientFormValues>({
    resolver: zodResolver(PatientSchema),
    defaultValues: { first_name: '', last_name: '', email: '', phone: '', notes: '' },
  });

  // Prefill the booking form from the signed-in portal patient so punch-card
  // ownership (matched by email in book-as-patient) stays consistent.
  // Clinic staff sessions do not count — those users still see Sign in.
  useEffect(() => {
    if (!sessionEmail || !bookingData?.clinic_id) {
      setPortalPatientId(null);
      return;
    }

    const prefill = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setPortalPatientId(null);
        return;
      }

      const { data: patients } = await supabase
        .from('patients')
        .select('id, first_name, last_name, email, phone, telephone_subscribers')
        .eq('portal_user_id', user.id)
        .eq('clinic_id', bookingData.clinic_id)
        .eq('status', 'active')
        .limit(1);

      const patient = patients?.[0];
      if (patient) {
        setPortalPatientId(patient.id);
        form.setValue('first_name', patient.first_name ?? '');
        form.setValue('last_name', patient.last_name ?? '');
        form.setValue('email', patient.email ?? user.email ?? '');
        const primary = primaryTelephoneNumber(patient.telephone_subscribers, patient.phone);
        if (primary) form.setValue('phone', primary);
      } else {
        setPortalPatientId(null);
        if (user.email) form.setValue('email', user.email);
      }
    };

    void prefill();
  }, [sessionEmail, bookingData?.clinic_id, form]);

  // ─── Navigation helpers ────────────────────────────────────────────────────

  const goToStep = useCallback((s: Step) => setStep(s), []);

  // ─── Date utilities ────────────────────────────────────────────────────────

  const advanceDays = bookingData?.settings.advance_days ?? 60;
  const exceptionsByDate = new Map(
    (bookingData?.settings.hours_exceptions ?? []).map((row) => [
      row.date,
      exceptionFromPublic(row),
    ]),
  );
  const datePractitioners = (
    showPractitionerNames && !anyClinicianSelected && selectedClinician
      ? eligibleClinicians.filter((clinician) => clinician.id === selectedClinician.id)
      : eligibleClinicians
  ).map(catalogClinicianToSchedule);
  const availableDates = listBookablePractitionerDates({
    practitioners: datePractitioners,
    clinicExceptions: exceptionsByDate,
    advanceDays,
  });
  const closedDates = new Set(
    (bookingData?.settings.hours_exceptions ?? [])
      .filter((row) => row.exception_type === 'closed')
      .map((row) => row.date),
  );
  const todayCivil = clinicCivilDate(new Date());
  const todayMonday = mondayOf(todayCivil);
  const lastBookableCivil = addCivilDays(todayCivil, advanceDays);
  const availableCivilDates = availableDates.map((day) => clinicCivilDate(day));

  useEffect(() => {
    if (availableCivilDates.length === 0) return;
    const firstWeek = firstBookableWeekMonday(availableCivilDates, todayMonday);
    setWeekMonday((current) => (current === todayMonday && firstWeek !== current ? firstWeek : current));
  }, [availableCivilDates.join(','), todayMonday, selectedService?.id, effectiveClinicianId]);

  // ─── Submission ────────────────────────────────────────────────────────────

  const paymentMethods = parseBookingPaymentMethods(bookingData?.settings.payment_methods);
  const punchCardsEnabled = paymentMethods.punch_card;
  const applicablePunchCards = punchCardsEnabled
    ? selectApplicablePunchCards(punchCardData?.punch_cards ?? [], selectedService?.id ?? null)
    : [];
  const preferredPunchCard = pickPreferredPunchCard(applicablePunchCards);
  const payWithPunchCard = Boolean(
    punchCardsEnabled && selectedPayment === 'punch_card' && preferredPunchCard?.id,
  );
  const paymentContext = resolvePortalBookingPaymentContext({
    clinicPaymentTiming: bookingData?.settings.payment_timing,
    servicePaymentTiming: selectedService?.payment_timing,
    serviceRequirePrepayment: selectedService?.require_prepayment,
    depositAmountCents: selectedService?.deposit_amount_cents,
    priceCents: selectedService?.price_cents,
    methods: paymentMethods,
    applicablePunchCards,
  });
  const advancePaymentRequired = paymentContext.advancePaymentRequired;
  const chargeCents = paymentContext.chargeAmountCents;
  const portalPaymentChoices = paymentContext.choices;
  const clinicDefaultPayment = pickDefaultPortalPaymentChoice(
    portalPaymentChoices,
    paymentContext.paymentTiming,
  );
  const displayedPaymentChoices = orderPaymentChoicesDefaultFirst(
    portalPaymentChoices,
    clinicDefaultPayment,
  );
  const payWithStripe =
    (selectedPayment === 'card' || selectedPayment === 'mobilepay') && chargeCents > 0;

  useEffect(() => {
    if (portalPaymentChoices.length === 0) {
      if (selectedPayment !== null) setSelectedPayment(null);
      return;
    }
    if (selectedPayment && portalPaymentChoices.includes(selectedPayment)) return;
    if (isCheckingPunchCards && punchCardsEnabled && isAuthenticated) return;
    setSelectedPayment(
      pickDefaultPortalPaymentChoice(portalPaymentChoices, paymentContext.paymentTiming),
    );
  }, [
    portalPaymentChoices,
    selectedPayment,
    isCheckingPunchCards,
    punchCardsEnabled,
    isAuthenticated,
    paymentContext.paymentTiming,
  ]);

  const applyBookingError = (err: unknown) => {
    const code = (err as { code?: string }).code;
    if (code === 'conflict') {
      setBookingError(t('booking.error_slot_taken'));
    } else if (code === 'rate-limited') {
      setBookingError(t('booking.error_rate_limited'));
    } else if (code === 'gone') {
      setBookingError(t('booking.payment_expired'));
    } else if (code === 'invalid-input') {
      setBookingError(t('booking.error_invalid_input'));
    } else if (code === 'not-found') {
      setBookingError(t('booking.error_not_found'));
    } else if (code === 'unauthorized' || code === 'forbidden') {
      setBookingError(t('booking.error_unauthorized'));
    } else if (code === 'internal-error') {
      setBookingError(t('booking.error_server'));
    } else if (err instanceof TypeError || (err as Error).message?.includes('fetch')) {
      setBookingError(t('booking.error_network'));
    } else {
      setBookingError(t('booking.error_generic'));
    }
  };

  const settleBooking = async (values: PatientFormValues) => {
    if (!clinicSlug || !selectedService || !selectedSlot) return;
    if (submitLock.current) return;
    if (advancePaymentRequired && !selectedPayment) {
      setBookingError(t('booking.select_payment_method'));
      return;
    }
    if (selectedPayment === 'punch_card' && !preferredPunchCard?.id) {
      setBookingError(t('booking.error_invalid_input'));
      return;
    }

    submitLock.current = true;
    setBookingError(null);

    const clinicianForBooking = clinicianIdForOnlineBooking({
      showPractitioners: showPractitionerNames,
      anyClinicianSelected,
      selectedClinicianId: selectedClinician?.id,
      slotClinicianId: selectedSlot.clinician_id,
    });

    try {
      if (payWithStripe) {
        const intent = await createIntentMutation.mutateAsync({
          clinic_slug: clinicSlug,
          service_id: selectedService.id,
          clinician_id: clinicianForBooking,
          start_time: selectedSlot.start,
          patient: {
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            phone: values.phone ?? undefined,
            notes: values.notes ?? undefined,
          },
        });

        setBookingIntentId(intent.booking_intent_id);

        const payLink = await requestPayLinkMutation.mutateAsync(intent.booking_intent_id);
        setPayToken(payLink.pay_token);
        setPaidOnline(true);
        goToStep('payment');
        setTimeout(() => {
          window.location.href = payLink.checkout_url;
        }, 800);
        return;
      }

      await bookMutation.mutateAsync({
        clinic_slug: clinicSlug,
        service_id: selectedService.id,
        clinician_id: clinicianForBooking,
        start_time: selectedSlot.start,
        patient: {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone ?? undefined,
          notes: values.notes ?? undefined,
        },
        punch_card_id: payWithPunchCard ? preferredPunchCard!.id : undefined,
      });
      if (payWithPunchCard) {
        await queryClient.invalidateQueries({ queryKey: ['authenticated-punch-cards-booking'] });
      }
      setStep('confirmation');
    } catch (err: unknown) {
      applyBookingError(err);
    } finally {
      submitLock.current = false;
    }
  };

  const onSubmit = async (values: PatientFormValues) => {
    if (advancePaymentRequired && step !== 'payment_method') {
      setBookingError(null);
      goToStep('payment_method');
      return;
    }
    await settleBooking(values);
  };

  const resetBooking = () => {
    setStep('service');
    setSelectedService(null);
    setSelectedClinician(null);
    setAnyClinicianSelected(false);
    setSelectedDate(null);
    setSelectedSlot(null);
    setSelectedPayment(null);
    setBookingIntentId(null);
    setPaidOnline(false);
    setBookingError(null);
    setWeekMonday(mondayOf(clinicCivilDate(new Date())));
    form.reset();
  };

  // ─── Loading / error states ────────────────────────────────────────────────

  if (isLoadingServices) {
    return (
      <div className="pt">
        <div
          className="bk-main"
          style={{ display: 'grid', minHeight: '100vh', placeItems: 'center' }}
        >
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--muted)' }} />
        </div>
      </div>
    );
  }

  if (servicesError || !bookingData) {
    const isForbidden =
      servicesError instanceof Error &&
      (servicesError.message.includes('forbidden') ||
        servicesError.message.includes('not enabled'));

    return (
      <div className="pt">
        <main className="bk-main">
          <div className="card bk-card" style={{ padding: 32, textAlign: 'center' }}>
            <h1>{isForbidden ? t('booking.disabled_title') : t('booking.not_found_title')}</h1>
            <p className="sub">
              {isForbidden ? t('booking.disabled_subtitle') : t('booking.not_found_subtitle')}
            </p>
          </div>
        </main>
      </div>
    );
  }

  const clinicLogoSrc = resolveClinicLogoUrl(bookingData.clinic_logo);
  const clinicAddress = formatClinicAddress(
    (bookingData as { clinic_address?: unknown }).clinic_address,
  );
  const clinicPlace = clinicAddress || bookingData.clinic_name;
  const isProgressStep = !['payment', 'awaiting_payment', 'confirmation'].includes(step);
  const progressSteps = onlineBookingStepsWithoutHiddenPractitioner(
    advancePaymentRequired ? PREPAY_STEPS : STANDARD_STEPS.slice(0, 5),
    showPractitionerNames,
  );
  const showClinicianVisual = progressSteps.includes('clinician');

  const isSubmitting =
    bookMutation.isPending || createIntentMutation.isPending || requestPayLinkMutation.isPending;

  const handleSignIn = () => {
    const returnUrl = window.location.pathname + window.location.search;
    navigate(`/portal/login?from=${encodeURIComponent(returnUrl)}`);
  };

  const handleOpenPortal = () => {
    if (isAuthenticated) {
      navigate('/portal/dashboard');
      return;
    }
    const params = new URLSearchParams({ from: '/portal/dashboard' });
    if (clinicSlug) params.set('clinic_slug', clinicSlug);
    navigate(`/portal/login?${params.toString()}`);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setSessionEmail(null);
    setIsAuthenticated(false);
    setPortalPatientId(null);
    setSelectedPayment(null);
  };

  const assignedClinician =
    selectedSlot != null
      ? (eligibleClinicians.find((clinician) => clinician.id === selectedSlot.clinician_id) ?? null)
      : null;
  const summaryClinician = showPractitionerNames
    ? anyClinicianSelected
      ? assignedClinician
        ? `${assignedClinician.name} (første ledige)`
        : t('booking.any_clinician')
      : selectedClinician
        ? [selectedClinician.name, selectedClinician.title].filter(Boolean).join(', ')
        : 'Ikke valgt'
    : (assignedClinician?.name ?? '');
  const summaryTime = selectedSlot ? formatPortalDateTime(selectedSlot.start) : 'Ikke valgt';

  const stepLabels: Record<Step, string> = {
    service: t('booking.step_service'),
    clinician: t('booking.step_clinician'),
    date: t('booking.step_date'),
    slot: t('booking.step_slot'),
    patient: t('booking.step_patient'),
    payment_method: t('booking.step_payment'),
    payment: t('booking.step_payment'),
    awaiting_payment: t('booking.step_awaiting_payment'),
    confirmation: t('booking.step_confirmation'),
  };

  const visualSteps: { id: VisualStepId; label: string }[] = [
    { id: 'service', label: stepLabels.service },
    ...(showClinicianVisual ? [{ id: 'clinician' as const, label: stepLabels.clinician }] : []),
    { id: 'time', label: 'Tid' },
    { id: 'patient', label: 'Oplysninger' },
  ];
  const visualCurrent = visualStepIdFor(step);
  const visualIndex = visualCurrent
    ? visualSteps.findIndex((item) => item.id === visualCurrent)
    : -1;

  const selectService = (svc: BookableService) => {
    setSelectedService(svc);
    setSelectedClinician(null);
    setAnyClinicianSelected(!showPractitionerNames);
    setSelectedDate(null);
    setSelectedSlot(null);
    setSelectedPayment(null);
    setWeekMonday(todayMonday);
  };

  const continueFromService = () => {
    if (!selectedService) return;
    goToStep(showPractitionerNames ? 'clinician' : 'date');
  };

  const selectClinician = (clinician: BookableClinician | null, any: boolean) => {
    setSelectedClinician(clinician);
    setAnyClinicianSelected(any);
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const continueFromClinician = () => {
    if (!anyClinicianSelected && !selectedClinician) return;
    goToStep('date');
  };

  const pickSlot = (slot: AvailableSlot) => {
    setSelectedDate(clinicCivilDate(slot.start));
    setSelectedSlot(slot);
  };

  const continueFromTime = () => {
    if (!selectedSlot) return;
    goToStep('patient');
  };

  const footerLine = [bookingData.clinic_name, clinicAddress].filter(Boolean).join(' · ');

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="pt">
      <header className="bk-top">
        <div className="in">
          <div className="cl-brand">
            {clinicLogoSrc ? (
              <img className="cl-logo" src={clinicLogoSrc} alt={bookingData.clinic_name} />
            ) : (
              <ClinicFallbackMark />
            )}
            <div>
              <b>{bookingData.clinic_name}</b>
              <span>{t('booking.header_subtitle')}</span>
            </div>
          </div>
          <div className="r">
            {isAuthenticated ? (
              <>
                <button
                  type="button"
                  className="btn btn-tertiary"
                  data-testid="booking-open-portal"
                  onClick={handleOpenPortal}
                >
                  <PortalIcon name="clipboard" size="sm" />
                  {t('booking.open_portal')}
                </button>
                <button
                  type="button"
                  className="btn btn-tertiary"
                  onClick={() => void handleSignOut()}
                >
                  <PortalIcon name="logout" size="sm" />
                  {t('booking.sign_out')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-tertiary"
                  data-testid="booking-open-portal"
                  onClick={handleOpenPortal}
                >
                  <PortalIcon name="clipboard" size="sm" />
                  {t('booking.open_portal')}
                </button>
                <button
                  type="button"
                  className="btn btn-tertiary"
                  data-testid="booking-sign-in"
                  onClick={handleSignIn}
                >
                  <PortalIcon name="user" size="sm" />
                  {t('booking.sign_in')}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="bk-main" id="main">
        <div className="card bk-card">
          {step === 'confirmation' && selectedService && selectedSlot ? (
            <ConfirmationStep
              clinicName={bookingData.clinic_name}
              clinicPlace={clinicPlace}
              service={selectedService}
              selectedSlot={selectedSlot}
              clinicianLabel={
                showPractitionerNames
                  ? assignedClinician
                    ? [assignedClinician.name, assignedClinician.title].filter(Boolean).join(', ')
                    : summaryClinician
                  : (assignedClinician?.name ?? '')
              }
              showPractitioner={showPractitionerNames}
              wasPrepaid={advancePaymentRequired || payWithPunchCard || paidOnline}
              paidWithPunchCard={payWithPunchCard}
              chargeCents={chargeCents}
              selectedPayment={selectedPayment}
              isAuthenticated={isAuthenticated}
              patientEmail={form.getValues('email')}
              t={t}
              onBookMore={resetBooking}
            />
          ) : (
            <>
              {isProgressStep && (
                <VisualStepper
                  steps={visualSteps}
                  currentIndex={visualIndex}
                  ariaLabel={`Trin ${Math.max(visualIndex, 0) + 1} af ${visualSteps.length}`}
                />
              )}

              {step === 'payment' ? (
                <PaymentRedirectStep chargeCents={chargeCents} t={t} />
              ) : step === 'awaiting_payment' ? (
                <AwaitingPaymentStep
                  intentStatus={intentStatus?.status ?? 'payment_submitted'}
                  t={t}
                  onCancel={() => {
                    setStep('patient');
                    setBookingError(t('booking.payment_cancelled'));
                  }}
                />
              ) : (
                <div className="bk-grid">
                  <div className="bk-body">
                    {step !== 'service' && selectedService ? (
                      <div className="m-sum">
                        <div className="grow">
                          <b>
                            {selectedSlot
                              ? summaryTime
                              : bookingServiceSummaryLabel(
                                  selectedService.name,
                                  selectedService.duration_minutes,
                                )}
                          </b>
                          <span>
                            {selectedSlot
                              ? `${selectedService.name}${
                                  showPractitionerNames && summaryClinician
                                    ? ` · ${summaryClinician}`
                                    : ''
                                } · ${formatPortalKroner(selectedService.price_cents)}`
                              : `${
                                  showPractitionerNames && selectedClinician
                                    ? `${selectedClinician.name} · `
                                    : anyClinicianSelected && showPractitionerNames
                                      ? 'Første ledige · '
                                      : ''
                                }${formatPortalKroner(selectedService.price_cents)}`}
                          </span>
                        </div>
                        <button type="button" className="link" onClick={() => goToStep('service')}>
                          Ændr
                        </button>
                      </div>
                    ) : null}

                    {step === 'service' && (
                      <>
                        <ServiceStep
                          services={bookingData.services}
                          selectedId={selectedService?.id ?? null}
                          clinicPaymentTiming={bookingData.settings.payment_timing}
                          t={t}
                          onSelect={selectService}
                        />
                        {punchCardsEnabled && !isAuthenticated ? (
                          <div className="notice teal" style={{ marginTop: 16 }}>
                            <PortalIcon name="ticket" size="sm" />
                            <span>
                              Har du et klippekort?{' '}
                              <button type="button" className="link" onClick={handleSignIn}>
                                {t('booking.sign_in')}
                              </button>{' '}
                              for at bruge det.
                            </span>
                          </div>
                        ) : null}
                        {punchCardsEnabled &&
                          (bookingData.punch_card_templates?.length ?? 0) > 0 && (
                            <PunchCardShopStep
                              templates={bookingData.punch_card_templates ?? []}
                              isAuthenticated={isAuthenticated}
                              buyingTemplateId={buyingTemplateId}
                              isBuying={punchCardPurchase.isPending}
                              t={t}
                              onBuy={(id) => void handleBuyPunchCard(id)}
                            />
                          )}
                        <div className="bk-acts">
                          <span />
                          <button
                            type="button"
                            className="btn btn-primary btn-lg"
                            disabled={!selectedService}
                            onClick={continueFromService}
                          >
                            Fortsæt
                          </button>
                        </div>
                      </>
                    )}

                    {step === 'clinician' && selectedService && showPractitionerNames && (
                      <ClinicianStep
                        clinicians={eligibleClinicians}
                        anySelected={anyClinicianSelected && !selectedClinician}
                        selectedId={selectedClinician?.id ?? null}
                        t={t}
                        onSelect={(clinician) => selectClinician(clinician, false)}
                        onAny={() => selectClinician(null, true)}
                        onContinue={continueFromClinician}
                        onBack={() => goToStep('service')}
                      />
                    )}

                    {showWeekPicker && (
                      <TimeStep
                        weekMonday={weekMonday}
                        weekDays={weekDays}
                        slotsByDate={slotsByDate}
                        closedDates={closedDates}
                        selectedStart={selectedSlot?.start ?? null}
                        isLoading={isLoadingWeekSlots}
                        hasAnyDates={availableDates.length > 0}
                        canGoPrev={weekMonday > todayMonday}
                        canGoNext={addCivilDays(weekMonday, 7) <= lastBookableCivil}
                        showPractitionerNames={showPractitionerNames}
                        clinicians={eligibleClinicians}
                        anyClinicianSelected={anyClinicianSelected}
                        selectedClinicianId={selectedClinician?.id ?? null}
                        today={todayCivil}
                        t={t}
                        onSelect={pickSlot}
                        onContinue={continueFromTime}
                        onPrevWeek={() => setWeekMonday(addCivilDays(weekMonday, -7))}
                        onNextWeek={() => setWeekMonday(addCivilDays(weekMonday, 7))}
                        onPickAny={() => selectClinician(null, true)}
                        onPickClinician={(clinician) => selectClinician(clinician, false)}
                        onBack={() => goToStep(showPractitionerNames ? 'clinician' : 'service')}
                      />
                    )}

                    {step === 'patient' && selectedService && selectedSlot && (
                      <PatientStep
                        form={form}
                        bookingError={bookingError}
                        isSubmitting={isSubmitting}
                        requiresPrepayment={advancePaymentRequired}
                        chargeCents={chargeCents}
                        punchCardsEnabled={punchCardsEnabled}
                        t={t}
                        onSubmit={onSubmit}
                        onBack={() => {
                          if (selectedDate) setWeekMonday(mondayOf(selectedDate));
                          goToStep('date');
                        }}
                        isAuthenticated={isAuthenticated}
                        preferredPunchCard={preferredPunchCard}
                        isCheckingPunchCards={isCheckingPunchCards}
                        paymentChoices={advancePaymentRequired ? [] : displayedPaymentChoices}
                        selectedPayment={selectedPayment}
                        defaultPayment={clinicDefaultPayment}
                        setSelectedPayment={setSelectedPayment}
                        onSignIn={handleSignIn}
                        deadlineHours={bookingData.settings.cancellation_deadline_hours ?? 24}
                        reschedulingAllowed={
                          bookingData.settings.allow_patient_rescheduling !== false
                        }
                      />
                    )}

                    {step === 'payment_method' && selectedService && selectedSlot && (
                      <PaymentMethodStep
                        bookingError={bookingError}
                        isSubmitting={isSubmitting}
                        chargeCents={chargeCents}
                        punchCardsEnabled={punchCardsEnabled}
                        t={t}
                        isAuthenticated={isAuthenticated}
                        preferredPunchCard={preferredPunchCard}
                        isCheckingPunchCards={isCheckingPunchCards}
                        paymentChoices={displayedPaymentChoices}
                        selectedPayment={selectedPayment}
                        defaultPayment={clinicDefaultPayment}
                        setSelectedPayment={setSelectedPayment}
                        onSignIn={handleSignIn}
                        onConfirm={() => void onSubmit(form.getValues())}
                        onBack={() => {
                          setBookingError(null);
                          goToStep('patient');
                        }}
                      />
                    )}
                  </div>

                  <aside className="bk-aside" aria-label="Din booking">
                    <h2>Din booking</h2>
                    <div className="sum">
                      <SummaryRow
                        icon="clipboard"
                        label="Behandling"
                        value={
                          selectedService
                            ? bookingServiceSummaryLabel(
                                selectedService.name,
                                selectedService.duration_minutes,
                              )
                            : 'Ikke valgt'
                        }
                        placeholder={!selectedService}
                      />
                      {showPractitionerNames ? (
                        <SummaryRow
                          icon="user"
                          label="Behandler"
                          value={summaryClinician}
                          placeholder={!selectedClinician && !anyClinicianSelected}
                        />
                      ) : null}
                      <SummaryRow
                        icon="calendar"
                        label="Tid"
                        value={summaryTime}
                        placeholder={!selectedSlot}
                      />
                      <SummaryRow icon="pin" label="Sted" value={clinicPlace} />
                      {selectedService && (step === 'patient' || step === 'payment_method') ? (
                        <>
                          <SummaryRow
                            icon="wallet"
                            label="Betaling"
                            value={paymentSummaryLabel(selectedPayment, t)}
                          />
                          <div className="sum-total">
                            <span>I alt</span>
                            <span className="num">
                              {formatPortalKroner(selectedService.price_cents)}
                            </span>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </aside>
                </div>
              )}
            </>
          )}
        </div>
        <p className="help" style={{ textAlign: 'center', marginTop: 20 }}>
          {footerLine}
        </p>
        <p className="help" style={{ textAlign: 'center', fontSize: 12 }}>
          Drevet af <b style={{ color: 'var(--teal)' }}>Clinio</b>
        </p>
      </main>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function VisualStepper({
  steps,
  currentIndex,
  ariaLabel,
}: {
  steps: { id: VisualStepId; label: string }[];
  currentIndex: number;
  ariaLabel: string;
}) {
  return (
    <nav className="stepper" aria-label={ariaLabel}>
      {steps.map((item, index) => {
        const done = index < currentIndex;
        const current = index === currentIndex;
        return (
          <span key={item.id} style={{ display: 'contents' }}>
            {index > 0 ? <span className={`stp-line${done || current ? ' done' : ''}`} /> : null}
            <div
              className={`stp${done ? ' done' : ''}${current ? ' cur' : ''}`}
              aria-current={current ? 'step' : undefined}
            >
              <span className="n">{done ? <PortalIcon name="check" size="xs" /> : index + 1}</span>
              <span className="lb">{item.label}</span>
            </div>
          </span>
        );
      })}
    </nav>
  );
}

function SummaryRow({
  icon,
  label,
  value,
  placeholder = false,
}: {
  icon: PortalIconName;
  label: string;
  value: string;
  placeholder?: boolean;
}) {
  return (
    <div className="kv">
      <PortalIcon name={icon} size="sm" />
      <div className="grow">
        <div className="k">{label}</div>
        <div className={cn('v', placeholder && 'ph')}>{value || 'Ikke valgt'}</div>
      </div>
    </div>
  );
}

function ServiceStep({
  services,
  selectedId,
  clinicPaymentTiming,
  t,
  onSelect,
}: {
  services: BookableService[];
  selectedId: string | null;
  clinicPaymentTiming?: unknown;
  t: Translate;
  onSelect: (s: BookableService) => void;
}) {
  return (
    <>
      <h1>Hvad vil du booke?</h1>
      <p className="sub">Vælg en behandling. Pris og varighed står ved hver.</p>
      {services.length === 0 ? (
        <p className="help" style={{ marginTop: 20 }}>
          {t('booking.no_services')}
        </p>
      ) : (
        <div className="svc">
          {services.map((svc) => (
            <button
              key={svc.id}
              type="button"
              className={cn('svc-i', selectedId === svc.id && 'on')}
              aria-pressed={selectedId === svc.id}
              onClick={() => onSelect(svc)}
            >
              <div className="grow">
                <div className="t">{svc.name}</div>
                {svc.description ? <div className="s">{svc.description}</div> : null}
                <div className="meta">
                  <span>
                    <PortalIcon name="clock" size="xs" />
                    {svc.duration_minutes} {t('booking.minutes')}
                  </span>
                </div>
              </div>
              <div className="pr">
                <b className="num">
                  {svc.price_cents > 0 ? formatPortalKroner(svc.price_cents) : '—'}
                </b>
                <span>{servicePaymentWhenLabel(svc, clinicPaymentTiming)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function PunchCardShopStep({
  templates,
  isAuthenticated,
  buyingTemplateId,
  isBuying,
  t,
  onBuy,
}: {
  templates: BookablePunchCardTemplate[];
  isAuthenticated: boolean;
  buyingTemplateId: string | null;
  isBuying: boolean;
  t: Translate;
  onBuy: (templateId: string) => void;
}) {
  return (
    <div style={{ marginTop: 32 }}>
      <h2 className="card-h" style={{ padding: 0 }}>
        {t('booking.punch_card_shop_title')}
      </h2>
      <p className="sub">{t('booking.punch_card_shop_subtitle')}</p>
      <div className="svc" style={{ marginTop: 16 }}>
        {templates.map((template) => {
          const buying = buyingTemplateId === template.id;
          return (
            <div key={template.id} className="svc-i">
              <span className="ic-tile">
                <PortalIcon name="ticket" />
              </span>
              <div className="grow">
                <div className="t">{template.name}</div>
                {template.description ? <div className="s">{template.description}</div> : null}
                <div className="meta">
                  <span>
                    {template.total_clips} {t('booking.punch_card_clips')}
                    {template.expires_months
                      ? ` · ${t('booking.punch_card_valid_months', { count: template.expires_months })}`
                      : ''}
                  </span>
                </div>
              </div>
              <div className="pr">
                <b className="num">{formatPortalKroner(template.price_cents)}</b>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={isBuying}
                  onClick={() => onBuy(template.id)}
                  style={{ marginTop: 8 }}
                >
                  {buying ? <Loader2 className="animate-spin" /> : null}
                  {isAuthenticated
                    ? t('booking.punch_card_buy')
                    : t('booking.punch_card_buy_sign_in')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ClinicianStep({
  clinicians,
  anySelected,
  selectedId,
  t,
  onSelect,
  onAny,
  onContinue,
  onBack,
}: {
  clinicians: BookableClinician[];
  anySelected: boolean;
  selectedId: string | null;
  t: Translate;
  onSelect: (c: BookableClinician) => void;
  onAny: () => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  return (
    <>
      <h1>Hvem vil du tale med?</h1>
      <p className="sub">Vælg &quot;Første ledige&quot;, hvis du vil have den tidligste tid.</p>
      <div className="prac">
        <button
          type="button"
          className={cn('svc-i', 'first', anySelected && 'on')}
          aria-pressed={anySelected}
          aria-label={t('booking.any_clinician')}
          onClick={onAny}
        >
          <span className="ic-tile" style={{ width: 48, height: 48, borderRadius: '50%' }}>
            <PortalIcon name="first" />
          </span>
          <div className="grow">
            <div className="t">Første ledige</div>
            <div className="s">{t('booking.any_clinician_desc')}</div>
          </div>
        </button>
        {clinicians.map((clinician, index) => {
          const on = selectedId === clinician.id;
          return (
            <button
              key={clinician.id}
              type="button"
              className={cn('svc-i', on && 'on')}
              aria-pressed={on}
              onClick={() => onSelect(clinician)}
            >
              {clinician.photo_url ? (
                <img src={clinician.photo_url} alt="" className="avatar av48" />
              ) : (
                <span className={`avatar av48 ${avatarTone(index)}`}>
                  {clinicianInitials(clinician.name)}
                </span>
              )}
              <div className="grow">
                <div className="t">{clinician.name}</div>
                {clinician.title ? <div className="s">{clinician.title}</div> : null}
              </div>
            </button>
          );
        })}
      </div>
      <div className="bk-acts">
        <button type="button" className="btn btn-tertiary" onClick={onBack}>
          <PortalIcon name="chevronLeft" size="sm" />
          {t('booking.back')}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-lg"
          disabled={!anySelected && !selectedId}
          onClick={onContinue}
        >
          Fortsæt
        </button>
      </div>
    </>
  );
}

function TimeStep({
  weekMonday,
  weekDays,
  slotsByDate,
  closedDates,
  selectedStart,
  isLoading,
  hasAnyDates,
  canGoPrev,
  canGoNext,
  showPractitionerNames,
  clinicians,
  anyClinicianSelected,
  selectedClinicianId,
  t,
  onSelect,
  onPrevWeek,
  onNextWeek,
  onPickAny,
  onPickClinician,
  onContinue,
  onBack,
  today,
}: {
  weekMonday: string;
  weekDays: string[];
  slotsByDate: Map<string, AvailableSlot[]>;
  closedDates: Set<string>;
  selectedStart: string | null;
  isLoading: boolean;
  hasAnyDates: boolean;
  canGoPrev: boolean;
  canGoNext: boolean;
  showPractitionerNames: boolean;
  clinicians: BookableClinician[];
  anyClinicianSelected: boolean;
  selectedClinicianId: string | null;
  today: string;
  t: Translate;
  onSelect: (slot: AvailableSlot) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onPickAny: () => void;
  onPickClinician: (clinician: BookableClinician) => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  return (
    <>
      <h1>Vælg tid</h1>
      <p className="sub">
        Hver tid står én gang. Med &quot;Første ledige&quot; vælger vi behandleren for dig.
      </p>
      {showPractitionerNames ? (
        <div className="who-row" role="group" aria-label="Behandler">
          <button
            type="button"
            className={cn('pill', 'txt', anyClinicianSelected && 'on')}
            aria-pressed={anyClinicianSelected}
            onClick={onPickAny}
          >
            <PortalIcon name="first" size="sm" />
            Første ledige
          </button>
          {clinicians.map((clinician, index) => {
            const on = !anyClinicianSelected && selectedClinicianId === clinician.id;
            return (
              <button
                key={clinician.id}
                type="button"
                className={cn('pill', on && 'on')}
                aria-pressed={on}
                onClick={() => onPickClinician(clinician)}
              >
                {clinician.photo_url ? (
                  <img src={clinician.photo_url} alt="" className="avatar" />
                ) : (
                  <span className={`avatar ${avatarTone(index)}`}>
                    {clinicianInitials(clinician.name)}
                  </span>
                )}
                {clinician.name}
              </button>
            );
          })}
        </div>
      ) : null}
      {!hasAnyDates ? (
        <p className="help" style={{ marginTop: 20 }}>
          {t('booking.no_dates')}
        </p>
      ) : (
        <>
          {isLoading ? (
            <p className="help" style={{ marginTop: 16 }}>
              Henter tider…
            </p>
          ) : null}
          <WeekSlotPicker
            days={weekDays}
            weekMonday={weekMonday}
            slotsByDate={slotsByDate}
            closedDates={closedDates}
            selectedStart={selectedStart}
            onSelect={onSelect}
            onPrevWeek={onPrevWeek}
            onNextWeek={onNextWeek}
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            today={today}
          />
        </>
      )}
      <div className="bk-acts">
        <button type="button" className="btn btn-tertiary" onClick={onBack}>
          <PortalIcon name="chevronLeft" size="sm" />
          {t('booking.back')}
        </button>
        <button type="button" className="btn btn-primary btn-lg" disabled={!selectedStart} onClick={onContinue}>
          Fortsæt
        </button>
      </div>
    </>
  );
}

function PatientStep({
  form,
  bookingError,
  isSubmitting,
  requiresPrepayment,
  chargeCents,
  punchCardsEnabled,
  t,
  onSubmit,
  onBack,
  isAuthenticated,
  preferredPunchCard,
  isCheckingPunchCards,
  paymentChoices,
  selectedPayment,
  defaultPayment,
  setSelectedPayment,
  onSignIn,
  deadlineHours,
  reschedulingAllowed,
}: {
  form: ReturnType<typeof useForm<PatientFormValues>>;
  bookingError: string | null;
  isSubmitting: boolean;
  requiresPrepayment: boolean;
  chargeCents: number;
  punchCardsEnabled: boolean;
  t: Translate;
  onSubmit: (v: PatientFormValues) => Promise<void>;
  onBack: () => void;
  isAuthenticated: boolean;
  preferredPunchCard: { id: string; name: string; remaining_clips: number } | null;
  isCheckingPunchCards: boolean;
  paymentChoices: PortalBookingPaymentChoice[];
  selectedPayment: PortalBookingPaymentChoice | null;
  defaultPayment: PortalBookingPaymentChoice | null;
  setSelectedPayment: (v: PortalBookingPaymentChoice) => void;
  onSignIn: () => void;
  deadlineHours: number;
  reschedulingAllowed: boolean;
}) {
  const showPaymentMethods = paymentChoices.length > 0;

  return (
    <>
      <h1>Dine oplysninger</h1>
      <p className="sub">Vi bruger dem til at bekræfte tiden og kontakte dig, hvis den ændres.</p>

      {requiresPrepayment ? (
        <div className="notice amber" style={{ marginTop: 16 }}>
          <PortalIcon name="wallet" size="sm" />
          <span>
            {t('booking.payment_required_to_confirm')}
            {chargeCents > 0
              ? ` ${t('booking.prepayment_notice', { amount: formatPortalKroner(chargeCents) })}`
              : ''}
          </span>
        </div>
      ) : null}

      {!isAuthenticated ? (
        <div className="notice neutral" style={{ marginTop: 16 }}>
          <PortalIcon name="user" size="sm" />
          <span>
            Har du en konto?{' '}
            <button
              type="button"
              className="link"
              data-testid="booking-sign-in-patient"
              onClick={onSignIn}
            >
              {t('booking.sign_in')}
            </button>
            , så udfylder vi felterne, og du kan bruge dit klippekort.
          </span>
        </div>
      ) : null}

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="form-grid">
          <FormField
            label={t('booking.field_first_name')}
            error={form.formState.errors.first_name?.message}
          >
            <input
              {...form.register('first_name')}
              className="input"
              placeholder={t('booking.field_first_name_placeholder')}
              autoComplete="given-name"
            />
          </FormField>
          <FormField
            label={t('booking.field_last_name')}
            error={form.formState.errors.last_name?.message}
          >
            <input
              {...form.register('last_name')}
              className="input"
              placeholder={t('booking.field_last_name_placeholder')}
              autoComplete="family-name"
            />
          </FormField>
          <FormField label={t('booking.field_email')} error={form.formState.errors.email?.message}>
            <input
              {...form.register('email')}
              type="email"
              className="input"
              placeholder={t('booking.field_email_placeholder')}
              autoComplete="email"
              readOnly={isAuthenticated}
            />
            <span className="help">Bekræftelsen sendes hertil.</span>
          </FormField>
          <FormField label={t('booking.field_phone')}>
            <input
              {...form.register('phone')}
              type="tel"
              className="input"
              placeholder={t('booking.field_phone_placeholder')}
              autoComplete="tel"
            />
          </FormField>
          <FormField label={t('booking.field_notes')} className="full">
            <textarea
              {...form.register('notes')}
              rows={3}
              className="input"
              placeholder={t('booking.field_notes_placeholder')}
            />
          </FormField>
        </div>

        {isAuthenticated && punchCardsEnabled && isCheckingPunchCards ? (
          <div
            className="help"
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t('booking.checking_punch_cards')}</span>
          </div>
        ) : null}

        {showPaymentMethods ? (
          <fieldset style={{ border: 0, padding: 0, margin: '24px 0 0' }}>
            <legend className="lbl">{t('booking.payment_method_title')}</legend>
            <div className="pay">
              {paymentChoices.map((choice) => (
                <PaymentMethodOption
                  key={choice}
                  choice={choice}
                  selected={selectedPayment === choice}
                  isClinicDefault={defaultPayment === choice}
                  preferredPunchCard={preferredPunchCard}
                  chargeCents={chargeCents}
                  t={t}
                  onSelect={setSelectedPayment}
                />
              ))}
            </div>
          </fieldset>
        ) : null}

        {bookingError ? (
          <div className="notice amber" style={{ marginTop: 16 }}>
            <PortalIcon name="info" size="sm" />
            <span>{bookingError}</span>
          </div>
        ) : null}

        <p className="help" style={{ marginTop: 14 }}>
          {bookingCancellationHint({ deadlineHours, reschedulingAllowed })}
        </p>

        <div className="bk-acts">
          <button type="button" className="btn btn-tertiary" onClick={onBack}>
            <PortalIcon name="chevronLeft" size="sm" />
            {t('booking.back')}
          </button>
          <button type="submit" className="btn btn-primary btn-lg" disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="animate-spin" /> : null}
            {isSubmitting
              ? t('booking.submitting')
              : requiresPrepayment || selectedPayment === 'card' || selectedPayment === 'mobilepay'
                ? t('booking.submit_prepay')
                : t('booking.submit')}
          </button>
        </div>
      </form>
    </>
  );
}

function PaymentMethodStep({
  bookingError,
  isSubmitting,
  chargeCents,
  punchCardsEnabled,
  t,
  isAuthenticated,
  preferredPunchCard,
  isCheckingPunchCards,
  paymentChoices,
  selectedPayment,
  defaultPayment,
  setSelectedPayment,
  onSignIn,
  onConfirm,
  onBack,
}: {
  bookingError: string | null;
  isSubmitting: boolean;
  chargeCents: number;
  punchCardsEnabled: boolean;
  t: Translate;
  isAuthenticated: boolean;
  preferredPunchCard: { id: string; name: string; remaining_clips: number } | null;
  isCheckingPunchCards: boolean;
  paymentChoices: PortalBookingPaymentChoice[];
  selectedPayment: PortalBookingPaymentChoice | null;
  defaultPayment: PortalBookingPaymentChoice | null;
  setSelectedPayment: (v: PortalBookingPaymentChoice) => void;
  onSignIn: () => void;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const confirmDisabled =
    isSubmitting ||
    !selectedPayment ||
    (selectedPayment === 'punch_card' && !preferredPunchCard?.id);

  let submitLabel = t('booking.submit_prepay');
  if (isSubmitting) {
    submitLabel = t('booking.submitting');
  } else if (selectedPayment === 'punch_card') {
    submitLabel = t('booking.confirm_punch_card');
  }

  return (
    <>
      <h1>{t('booking.payment_step_title')}</h1>
      <p className="sub">{t('booking.select_payment_method')}</p>

      <div className="notice amber" style={{ marginTop: 16 }}>
        <PortalIcon name="wallet" size="sm" />
        <span>
          {t('booking.payment_required_to_confirm')}
          {chargeCents > 0
            ? ` ${t('booking.prepayment_required', { amount: formatPortalKroner(chargeCents) })}`
            : ''}
        </span>
      </div>

      {isAuthenticated && punchCardsEnabled && isCheckingPunchCards ? (
        <div
          className="help"
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{t('booking.checking_punch_cards')}</span>
        </div>
      ) : null}

      {paymentChoices.length > 0 ? (
        <fieldset style={{ border: 0, padding: 0, margin: '24px 0 0' }}>
          <legend className="lbl">{t('booking.payment_method_title')}</legend>
          <div className="pay">
            {paymentChoices.map((choice) => (
              <PaymentMethodOption
                key={choice}
                choice={choice}
                selected={selectedPayment === choice}
                isClinicDefault={defaultPayment === choice}
                preferredPunchCard={preferredPunchCard}
                chargeCents={chargeCents}
                t={t}
                onSelect={setSelectedPayment}
              />
            ))}
          </div>
        </fieldset>
      ) : null}

      {punchCardsEnabled && !isAuthenticated ? (
        <div className="notice neutral" style={{ marginTop: 16 }}>
          <PortalIcon name="ticket" size="sm" />
          <span>
            {t('booking.sign_in_for_punch_card')}{' '}
            <button
              type="button"
              className="link"
              data-testid="booking-sign-in-payment"
              onClick={onSignIn}
            >
              {t('booking.sign_in')}
            </button>
          </span>
        </div>
      ) : null}

      {bookingError ? (
        <div className="notice amber" style={{ marginTop: 16 }}>
          <PortalIcon name="info" size="sm" />
          <span>{bookingError}</span>
        </div>
      ) : null}

      <div className="bk-acts">
        <button type="button" className="btn btn-tertiary" onClick={onBack}>
          <PortalIcon name="chevronLeft" size="sm" />
          {t('booking.back')}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-lg"
          disabled={confirmDisabled}
          onClick={onConfirm}
        >
          {isSubmitting ? <Loader2 className="animate-spin" /> : null}
          {submitLabel}
        </button>
      </div>
    </>
  );
}

function paymentChoiceCopy(
  choice: PortalBookingPaymentChoice,
  t: Translate,
  preferredPunchCard: { id: string; name: string; remaining_clips: number } | null,
  chargeCents: number,
): { icon: PortalIconName; label: string; detail: string | null } {
  switch (choice) {
    case 'punch_card':
      return {
        icon: 'ticket',
        label: t('booking.use_punch_card'),
        detail: preferredPunchCard
          ? `${preferredPunchCard.name} · ${preferredPunchCard.remaining_clips} ${t('booking.clips_remaining')}`
          : null,
      };
    case 'card':
      return {
        icon: 'card',
        label: t('booking.pay_with_card'),
        detail: 'Visa, Mastercard eller Dankort.',
      };
    case 'mobilepay':
      return {
        icon: 'smartphone',
        label: t('booking.pay_with_mobilepay'),
        detail: 'Du sendes videre til MobilePay.',
      };
    case 'later':
      return {
        icon: 'wallet',
        label: t('booking.pay_at_appointment'),
        detail: `Du betaler ${formatPortalKroner(chargeCents)} på klinikken efter samtalen.`,
      };
    default:
      return assertNever(choice);
  }
}

function paymentSummaryLabel(choice: PortalBookingPaymentChoice | null, t: Translate): string {
  if (!choice) return 'Ikke valgt';
  switch (choice) {
    case 'punch_card':
      return t('booking.use_punch_card');
    case 'card':
      return t('booking.pay_with_card');
    case 'mobilepay':
      return t('booking.pay_with_mobilepay');
    case 'later':
      return 'Ved behandlingen';
    default:
      return assertNever(choice);
  }
}

function PaymentMethodOption({
  choice,
  selected,
  isClinicDefault,
  preferredPunchCard,
  chargeCents,
  t,
  onSelect,
}: {
  choice: PortalBookingPaymentChoice;
  selected: boolean;
  isClinicDefault: boolean;
  preferredPunchCard: { id: string; name: string; remaining_clips: number } | null;
  chargeCents: number;
  t: Translate;
  onSelect: (choice: PortalBookingPaymentChoice) => void;
}) {
  const copy = paymentChoiceCopy(choice, t, preferredPunchCard, chargeCents);
  return (
    <button
      type="button"
      className={cn('pay-i', selected && 'on')}
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect(choice)}
    >
      <span className={cn('radio', selected && 'on')} />
      <PortalIcon name={copy.icon} />
      <div className="grow">
        <div className="t">
          {copy.label}{' '}
          {isClinicDefault ? <span className="chip chip-teal">Klinikkens standard</span> : null}
        </div>
        {copy.detail ? <div className="s">{copy.detail}</div> : null}
        {choice === 'punch_card' && preferredPunchCard ? (
          <span className="sr">{preferredPunchCard.name}</span>
        ) : null}
      </div>
    </button>
  );
}

function PaymentRedirectStep({ chargeCents, t }: { chargeCents: number; t: Translate }) {
  return (
    <div className="ok-wrap">
      <div className="big-ic" style={{ background: 'var(--teal-tint)', color: 'var(--teal)' }}>
        <PortalIcon name="card" />
      </div>
      <h1>{t('booking.payment_redirect_title')}</h1>
      <p>{t('booking.payment_required_to_confirm')}</p>
      <p>
        {t('booking.payment_redirect_subtitle', {
          amount: formatPortalKroner(chargeCents),
        })}
      </p>
      <div style={{ marginTop: 24, display: 'grid', placeItems: 'center' }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--muted)' }} />
      </div>
      <p className="help" style={{ marginTop: 12 }}>
        {t('booking.payment_redirect_wait')}
      </p>
    </div>
  );
}

function AwaitingPaymentStep({
  intentStatus,
  t,
  onCancel,
}: {
  intentStatus: string;
  t: Translate;
  onCancel: () => void;
}) {
  return (
    <div className="ok-wrap">
      <div className="big-ic" style={{ background: 'var(--teal-tint)', color: 'var(--teal)' }}>
        <Loader2 className="h-7 w-7 animate-spin" />
      </div>
      <h1>{t('booking.awaiting_payment_title')}</h1>
      <p>{t('booking.awaiting_payment_subtitle')}</p>
      {intentStatus === 'payment_submitted' ? (
        <p className="help" style={{ marginTop: 16 }}>
          {t('booking.awaiting_payment_note')}
        </p>
      ) : null}
      <button
        type="button"
        className="btn btn-tertiary"
        style={{ marginTop: 24 }}
        onClick={onCancel}
      >
        {t('booking.awaiting_payment_cancel')}
      </button>
    </div>
  );
}

function confirmationPaymentLabel(
  paidWithPunchCard: boolean,
  wasPrepaid: boolean,
  selectedPayment: PortalBookingPaymentChoice | null,
  chargeCents: number,
  t: Translate,
): string {
  if (paidWithPunchCard) {
    return `${t('booking.confirmed_paid')} · ${t('booking.confirmed_payment_punch_card')}`;
  }
  if (!selectedPayment) {
    return wasPrepaid
      ? `${t('booking.confirmed_paid')} · ${formatPortalKroner(chargeCents)}`
      : `${formatPortalKroner(chargeCents)} · betales ved behandlingen`;
  }
  switch (selectedPayment) {
    case 'punch_card':
      return `${t('booking.confirmed_paid')} · ${t('booking.confirmed_payment_punch_card')}`;
    case 'card':
      return `${formatPortalKroner(chargeCents)} · ${t('booking.pay_with_card')}`;
    case 'mobilepay':
      return `${formatPortalKroner(chargeCents)} · ${t('booking.pay_with_mobilepay')}`;
    case 'later':
      return `${formatPortalKroner(chargeCents)} · betales ved behandlingen`;
    default:
      return assertNever(selectedPayment);
  }
}

function ConfirmationStep({
  clinicName,
  clinicPlace,
  service,
  selectedSlot,
  clinicianLabel,
  showPractitioner,
  wasPrepaid,
  paidWithPunchCard,
  chargeCents,
  selectedPayment,
  isAuthenticated,
  patientEmail,
  t,
  onBookMore,
}: {
  clinicName: string;
  clinicPlace: string;
  service: BookableService;
  selectedSlot: AvailableSlot;
  clinicianLabel: string;
  showPractitioner: boolean;
  wasPrepaid: boolean;
  paidWithPunchCard: boolean;
  chargeCents: number;
  selectedPayment: PortalBookingPaymentChoice | null;
  isAuthenticated: boolean;
  patientEmail: string;
  t: Translate;
  onBookMore: () => void;
}) {
  return (
    <div className="ok-wrap">
      <div
        className="big-ic"
        style={{ background: 'var(--green-tint)', color: 'var(--green-ink)' }}
      >
        <PortalIcon name="check" />
      </div>
      <h1>{t('booking.confirmed_title')}</h1>
      <p>
        {paidWithPunchCard
          ? t('booking.confirmed_punch_card_subtitle')
          : wasPrepaid
            ? t('booking.confirmed_prepaid_subtitle')
            : t('booking.confirmed_subtitle')}
      </p>
      {patientEmail ? (
        <p>Vi har sendt en bekræftelse til {patientEmail}.</p>
      ) : (
        <p>{t('booking.email_sent_note')}</p>
      )}
      {!patientEmail ? null : <p className="sr">{t('booking.email_sent_note')}</p>}

      <div className="ok-sum">
        <SummaryRow
          icon="calendar"
          label={t('booking.confirmed_time')}
          value={formatPortalDateTimeRange(selectedSlot.start, selectedSlot.end)}
        />
        <SummaryRow
          icon="clipboard"
          label={t('booking.confirmed_service')}
          value={bookingServiceSummaryLabel(service.name, service.duration_minutes)}
        />
        {showPractitioner && clinicianLabel ? (
          <SummaryRow icon="user" label="Behandler" value={clinicianLabel} />
        ) : null}
        <SummaryRow
          icon="pin"
          label={t('booking.confirmed_clinic')}
          value={clinicPlace || clinicName}
        />
        <SummaryRow
          icon="wallet"
          label={t('booking.confirmed_payment')}
          value={confirmationPaymentLabel(
            paidWithPunchCard,
            wasPrepaid,
            selectedPayment,
            chargeCents,
            t,
          )}
        />
      </div>

      <div className="ok-acts">
        <button type="button" className="btn btn-primary" onClick={onBookMore}>
          Book en tid mere
        </button>
        {isAuthenticated ? (
          <Link to="/portal/dashboard" className="btn btn-secondary">
            Åbn portal
          </Link>
        ) : null}
      </div>
      {isAuthenticated ? (
        <p className="help" style={{ marginTop: 20 }}>
          <Link to="/portal/messages" className="link">
            Skriv til klinikken
          </Link>
        </p>
      ) : null}
    </div>
  );
}

function FormField({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={['field', className].filter(Boolean).join(' ')}>
      <label>{label}</label>
      {children}
      {error ? (
        <p className="help" style={{ color: 'var(--red-ink)' }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
